import { FormikErrors, useFormik } from "formik";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { useTranslation } from "react-i18next";
import ArfInputTextarea from "../../components/ArfComponents/ArfInputTextarea";
import { useQuery } from "@tanstack/react-query";
import sectorsService from "../../services/sectors/sectors.service";
import ArfInputNumber from "../../components/ArfComponents/ArfInputNumber";
import auditQuestionService from "../../services/audit-questions/audit-questions.service";
import {
  CreateAuditQuestionDto,
  UpdateAuditQuestionDto,
  UpsertAuditQuestionDto,
} from "../../services/audit-questions/audit-questions.dto";
import { FieldErrorMessage, IsFieldValid } from "../../utils/formValidator";
import classNames from "classnames";
import { Dropdown } from "primereact/dropdown";
import trimValues from "../../utils/trimValues";
import { useAxiosContext } from "../..";
import { AuditQuestionDto } from "../../services/audit-questions/audit-questions.dto";

interface AuditQuestionModalProps {
  visible: boolean;
  onHide: () => void;
  selectedAuditQuestion: AuditQuestionDto | undefined;
}

const AuditQuestionModal = ({
  visible,
  onHide,
  selectedAuditQuestion,
}: AuditQuestionModalProps) => {
  const { t } = useTranslation();
  const toaster = useAxiosContext();

  let questionInitialValues: UpsertAuditQuestionDto;
  if (selectedAuditQuestion === null || selectedAuditQuestion === undefined) {
    questionInitialValues = {
      id: 0,
      question: "",
      questionTR: "",
      questionCH: "",
      sectorId: undefined,
      standardId: undefined,
      defaultAnswerEN: "",
      defaultAnswerTR: "",
      defaultAnswerCH: "",
    };
  } else {
    questionInitialValues = {
      ...selectedAuditQuestion,
    };
  }

  const formik = useFormik<UpsertAuditQuestionDto>({
    initialValues: questionInitialValues,
    enableReinitialize: true,
    validate: data => {
      let errors: FormikErrors<UpsertAuditQuestionDto> = {};
      if (data.question === undefined || data.question === "") {
        errors.question = "Required";
      }
      if (data.questionTR === undefined || data.questionTR === "") {
        errors.questionTR = "Required";
      }
      if (data.questionCH === undefined || data.questionCH === "") {
        errors.questionCH = "Required";
      }
      if (data.sectorId === undefined) {
        errors.sectorId = "Required";
      }
      if (data.standardId === undefined) {
        errors.standardId = "Required";
      }

      return errors;
    },
    onSubmit: () => {
      if (questionInitialValues.id === 0) {
        var createAuditQuestionDto: CreateAuditQuestionDto = {
          question: formik.values.question,
          questionTR: formik.values.questionTR,
          questionCH: formik.values.questionCH,
          sectorId: formik.values.sectorId!,
          standardId: formik.values.standardId!,
          defaultAnswerEN: formik.values.defaultAnswerEN || "",
          defaultAnswerTR: formik.values.defaultAnswerTR || "",
          defaultAnswerCH: formik.values.defaultAnswerCH || "",
        };
        let trimmedValues: CreateAuditQuestionDto = trimValues(createAuditQuestionDto);
        auditQuestionService.Add(trimmedValues, { blockUI: true }).then(response => {
          if (!response.error) {
            onHide();
            formik.resetForm();
            toaster.showMessage(t("AuditQuestionAdded"), "success");
          }
        });
      } else {
        var updateAuditQuestionDto: UpdateAuditQuestionDto = {
          id: formik.values.id,
          question: formik.values.question,
          questionTR: formik.values.questionTR,
          questionCH: formik.values.questionCH,
          defaultAnswerEN: formik.values.defaultAnswerEN || "",
          defaultAnswerTR: formik.values.defaultAnswerTR || "",
          defaultAnswerCH: formik.values.defaultAnswerCH || "",
        };
        let trimmedValues: UpdateAuditQuestionDto = trimValues(updateAuditQuestionDto);
        auditQuestionService.Update(trimmedValues, { blockUI: true }).then(response => {
          if (!response.error) {
            onHide();
            formik.resetForm();
            toaster.showMessage(t("AuditQuestionEdited"), "success");
          }
        });
      }
    },
  });

  const { data: sectorsResponse } = useQuery({
    queryKey: ["sectors"],
    queryFn: () => sectorsService.GetAll(),
  });

  const { data: standardsResponse } = useQuery({
    queryKey: ["sectorId", formik.values.sectorId],
    queryFn: () => {
      if (formik.values.sectorId) return sectorsService.GetStandards(formik.values.sectorId);
    },
    enabled: !!formik.values.sectorId,
  });

  return (
    <div className="card flex justify-content-center">
      <Dialog
        draggable={false}
        visible={visible}
        onHide={() => {
          formik.resetForm();
          onHide();
        }}
        className="md:w-6 mx-4"
        header={
          <>
            <h2 className="text-primary text-xl font-semibold mt-5">
              {questionInitialValues.id === 0 ? t("AddAuditQuestion") : t("EditAuditQuestion")}
            </h2>
            <hr className="hr-color" />
          </>
        }
        footer={
          <div className="flex flex-row gap-3 justify-content-center md:justify-content-end mt-3">
            <Button
              label={t("Cancel")}
              className="px-8 m-0 justify-content-center"
              type="button"
              severity="secondary"
              onClick={() => {
                formik.resetForm();
                onHide();
              }}
            />

            <Button
              label={t("Save")}
              className="px-8 m-0 justify-content-center"
              onClick={() => formik.submitForm()}
            />
          </div>
        }
      >
        <form onSubmit={formik.handleSubmit}>
          <div className="grid">
            <div className="col-12 lg:col-6">
              <label
                htmlFor="sectorId"
                className={classNames("block mb-2 text-color font-bold", {
                  "p-error": IsFieldValid(formik, "sectorId"),
                })}
              >
                {t("Sector")}
              </label>

              <Dropdown
                value={formik.values.sectorId}
                onChange={e => {
                  formik.setFieldValue("standardId", undefined);
                  formik.setFieldValue("sectorId", e.value);
                }}
                disabled={!(selectedAuditQuestion === null || selectedAuditQuestion === undefined)}
                options={sectorsResponse?.result?.data}
                optionLabel="name"
                optionValue="id"
                placeholder={t("Select")}
                className={classNames("w-full", {
                  "p-invalid": IsFieldValid(formik, "sectorId"),
                })}
                emptyFilterMessage={() => {
                  return <>{t("NoAvailableOptions")}</>;
                }}
              />
              {FieldErrorMessage(formik, "sectorId")}
            </div>

            <div className="col-12 lg:col-6">
              <label
                className={classNames("block text-color font-bold mb-2", {
                  "p-error": IsFieldValid(formik, "standardId"),
                })}
              >
                {t("Standard")}
              </label>
              <Dropdown
                inputId="standardId"
                name="standardId"
                value={formik.values.standardId}
                options={standardsResponse?.result}
                optionLabel="name"
                optionValue="id"
                disabled={
                  !standardsResponse?.result ||
                  !(selectedAuditQuestion === null || selectedAuditQuestion === undefined)
                }
                placeholder={t("Select")}
                onChange={e => {
                  formik.setFieldValue("standardId", e.value);
                }}
                className={classNames("w-full", {
                  "p-invalid": IsFieldValid(formik, "standardId"),
                })}
              />
              {FieldErrorMessage(formik, "standardId")}
            </div>
            <div className="col-12 lg:col-6">
              <div className="grid">
                <ArfInputTextarea formik={formik} label="Question(EN)" name="question" className="col-12" />
                <ArfInputTextarea formik={formik} label="Question(TR)" name="questionTR" className="col-12" />
                <ArfInputTextarea formik={formik} label="Question(CH)" name="questionCH" className="col-12" />
              </div>
            </div>
            <div className="col-12 lg:col-6">
              <div className="grid">
                <ArfInputTextarea
                  formik={formik}
                  label="DefaultAnswer(EN)"
                  name="defaultAnswerEN"
                  className="col-12"
                />
                <ArfInputTextarea
                  formik={formik}
                  label="Default Answer(TR)"
                  name="defaultAnswerTR"
                  className="col-12"
                />
                <ArfInputTextarea
                  formik={formik}
                  label="Default Answer(CH)"
                  name="defaultAnswerCH"
                  className="col-12"
                />
              </div>
            </div>
          </div>
        </form>
      </Dialog>
    </div>
  );
};

export default AuditQuestionModal;
